import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { CalendarCheck, ArrowUpRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Book() {
  const { partnerId } = useParams();
  const [partner, setPartner] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    contact_name: "", contact_email: "", proposed_date: "", proposed_time: "",
    timezone_label: "", topic: "",
  });

  useEffect(() => {
    axios.get(`${API}/public/partner/${partnerId}`)
      .then(({ data }) => setPartner(data))
      .catch(() => setNotFound(true));
  }, [partnerId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.contact_email || !form.proposed_date || !form.proposed_time) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/partner/${partnerId}/book`, form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed");
    }
    setSubmitting(false);
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[color:var(--aii-bg)] flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <img src="/aii-logo.png" alt="Aii Group" className="w-16 h-16 mx-auto object-contain mb-4" />
          <h1 className="text-xl font-display font-semibold">This booking link is invalid.</h1>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-2">Please contact the person who sent you this link.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--aii-bg)]">
      <header className="bg-white border-b border-[color:var(--aii-border)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <img src="/aii-logo.png" alt="Aii Group" className="w-10 h-10 object-contain" />
          <div className="min-w-0">
            <div className="font-display text-base font-bold">AII GROUP<span className="text-brand">®</span></div>
            <div className="text-xs text-[color:var(--aii-muted)]">Meaningful Adventures</div>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        {done ? (
          <div className="aii-card p-8 text-center" data-testid="book-success">
            <CheckCircle2 size={44} className="text-brand mx-auto" />
            <h1 className="text-2xl font-display font-semibold mt-3">We&rsquo;ve got your meeting request.</h1>
            <p className="text-[color:var(--aii-text-2)] mt-2">
              Our BDOM will confirm shortly with a video-call link. Meanwhile, check your inbox for a confirmation email.
            </p>
            <a href="https://theaiigroupteam.com" target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-1 text-brand hover:text-brand-dark font-semibold">
              Learn more about Aii Group <ArrowUpRight size={14} />
            </a>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="text-xs uppercase tracking-widest text-brand font-semibold">Book a 20-min intro call</div>
              <h1 className="text-2xl sm:text-3xl font-display font-semibold mt-1 flex items-center gap-2"><CalendarCheck className="text-brand" /> Meet the Aii Group BDOM</h1>
              {partner && (
                <p className="text-sm text-[color:var(--aii-text-2)] mt-2">
                  For <b>{partner.company_name}</b>{partner.country ? ` · ${partner.country}` : ""} · propose a time that works for you.
                </p>
              )}
            </div>

            <form onSubmit={submit} className="aii-card p-6 space-y-4" data-testid="book-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-xs text-[color:var(--aii-muted)] mb-1">Your name *</div>
                  <input required data-testid="b-name" className="input-aii w-full" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                </label>
                <label className="block">
                  <div className="text-xs text-[color:var(--aii-muted)] mb-1">Email *</div>
                  <input required type="email" data-testid="b-email" className="input-aii w-full" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                </label>
                <label className="block">
                  <div className="text-xs text-[color:var(--aii-muted)] mb-1">Date *</div>
                  <input required type="date" data-testid="b-date" className="input-aii w-full" value={form.proposed_date} onChange={(e) => setForm({ ...form, proposed_date: e.target.value })} />
                </label>
                <label className="block">
                  <div className="text-xs text-[color:var(--aii-muted)] mb-1">Time (24 h) *</div>
                  <input required type="time" data-testid="b-time" className="input-aii w-full" value={form.proposed_time} onChange={(e) => setForm({ ...form, proposed_time: e.target.value })} />
                </label>
                <label className="block sm:col-span-2">
                  <div className="text-xs text-[color:var(--aii-muted)] mb-1">Time zone (optional)</div>
                  <input data-testid="b-tz" className="input-aii w-full" placeholder="e.g., GMT, EST, IST" value={form.timezone_label} onChange={(e) => setForm({ ...form, timezone_label: e.target.value })} />
                </label>
                <label className="block sm:col-span-2">
                  <div className="text-xs text-[color:var(--aii-muted)] mb-1">What would you like to discuss?</div>
                  <textarea data-testid="b-topic" className="input-aii w-full" rows={3} value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} />
                </label>
              </div>
              <button type="submit" data-testid="b-submit" disabled={submitting}
                className="w-full sm:w-auto px-6 py-3 bg-brand text-white rounded-md font-semibold hover:bg-brand-dark disabled:opacity-50">
                {submitting ? "Sending…" : "Request meeting"}
              </button>
              <p className="text-xs text-[color:var(--aii-muted)]">
                By submitting, you agree we may email you a confirmation and follow-up. We never share your details.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
